import { createSimpleSelector } from '../selector-utils';
import { MakeRef } from '../types';
import { createActionsWithReducer } from '../utils';
import { createEntityAdapter } from './adapter';
import {
  EntityCollectionStoreOptions,
  EntityCollectionStoreCustomActions,
  BaseEntity,
  EntityState,
  IdSelector,
  MetaOptions,
} from './types';

type IdSelectorOrDefault<
  T extends IdSelector<any> | undefined,
  U extends BaseEntity,
> = T extends IdSelector<any> ? T : (entity: U) => U['id'];

// eslint-disable-next-line max-lines-per-function
export function createEntityCollectionStore<
  T extends BaseEntity,
  S extends MetaOptions = MetaOptions,
>() {
  return <U extends EntityCollectionStoreOptions<T, S>>(namespace: string, options?: U) => {
    const ns = `entityCollection/${namespace}`;

    const baseSelector = createSimpleSelector(
      (root: any) => <EntityState<T>>root[ns],
      state => state,
    );
    // @ts-ignore
    const idSelector: IdSelectorOrDefault<U['idSelector'], T> =
      options?.idSelector || ((entity: T) => entity.id);

    const makeRef: MakeRef<T> = (mapFrom, mapTo, source) => ({ mapFrom, mapTo, source });
    const {
      adapter,
      initialState: defaultInitState,
      selectors,
      defaultActions,
    } = createEntityAdapter<T, IdSelectorOrDefault<U['idSelector'], T>, S>(
      baseSelector,
      ns,
      idSelector,
      options?.references?.(makeRef),
    );
    const initialState = options?.initialState || defaultInitState;

    const mergedActions = <typeof defaultActions & EntityCollectionStoreCustomActions<U>>{
      ...defaultActions,
      ...(options?.customActions?.(adapter) || {}),
    };

    const { actions, reducer } = createActionsWithReducer(namespace, initialState, mergedActions);
    return {
      namespace: ns,
      selectors,
      actions,
      reducer,
      initialState,
    };
  };
}
